import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Spinner, Text } from '@geist-ui/core';
import AuthService from '../../services/auth.service';

/**
 * ProtectedRoute component to protect routes that require authentication
 * Redirects to login page if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading, isPatron } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (user && !loading) {
      // Skip Patreon verification if user has forced access
      if (user.is_forced === true) {
        return;
      }

      console.log('ProtectedRoute: Verifying Patreon access for user', user.fullName || user.email);
      const verifyPatreonAccess = async () => {
        try {
          await AuthService.verifyPatreonAccess();
          console.log('ProtectedRoute: User has valid Patreon access');
        } catch (error) {
          console.log('ProtectedRoute: Error verifying Patreon access', error);
          if (error.patreonRequired) {
            console.log('ProtectedRoute: Redirecting to subscription-required page');
            navigate('/subscription-required', { state: { from: location }, replace: true });
          }
        }
      };
      
      verifyPatreonAccess();
    }
  }, [user, loading, navigate, location]);

  // Show loading spinner while checking auth status
  if (loading) {
    console.log('ProtectedRoute: Loading auth status...');
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}>
        <Spinner size="large" />
        <Text p style={{ marginTop: '1rem' }}>Checking authentication status...</Text>
      </div>
    );
  }

  if (!user) {
    console.log('ProtectedRoute: No user, redirecting to landing page');
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!isPatron()) {
    console.log('ProtectedRoute: User is not a patron, redirecting to subscription-required page');
    return <Navigate to="/subscription-required" state={{ from: location }} replace />;
  }

  console.log('ProtectedRoute: User is authenticated and is a patron, rendering children');
  return children;
};

export default ProtectedRoute; 